// Retail-POS style item search: one box that takes a typed name OR a scanned /
// typed barcode, and puts the best match first. Used by the Items list, the POS
// grid and Stock Details so they all find things the same way.
//
// Rank (lower = better):
//   0  barcode is exactly what was typed/scanned
//   1  barcode starts with it - only when the text looks like a code (has a digit
//      or - _ . /), so typing the name "tom" is not hijacked by a barcode "TOM-1"
//   2  name is exactly it
//   3  name starts with it
//   4  every typed word starts a word of the name         ("tom pur" -> "Tomato Puree")
//   5  name or barcode contains it, or the text typed without spaces matches the
//      name without spaces                                  ("redbul" -> "Red Bull Can")
//   6  every typed word is inside the NAME, any order (1-2 letter words must start
//      a name word, so "a" does not match "banana")
//   7  same, but the CATEGORY name counts too
//   8  typo-tolerant match - ONLY when nothing matched above, and strict: every
//      typed word is 4+ letters and is within 1-3 letter slips of a name word that
//      starts with the same letter (so "tomatoo" finds Tomato, "xyzzy" finds nothing)
// Matching ignores case, accents, extra spaces and punctuation, and works with
// Thai text (combining marks are kept together with their letters).

// Accents on Latin letters are stripped (NFKD + remove U+0300-036F); Thai
// combining marks are NOT in that range, so Thai text is left intact.
export const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const wordsOf = (normalized) =>
  normalized.split(/[^\p{L}\p{N}\p{M}]+/u).filter(Boolean);

// Letters and digits only - for matching "redbul" against "red bull".
const compact = (normalized) => normalized.replace(/[^\p{L}\p{N}\p{M}]+/gu, "");

// A scanned/typed code: no spaces, and a digit or a code separator in it.
const looksLikeCode = (query) => !/\s/.test(query) && (/\d/.test(query) || /[-_./]/.test(query));

const levenshtein = (a, b) => {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      cur[j] = a[i - 1] === b[j - 1] ? prev[j - 1] : 1 + Math.min(prev[j - 1], prev[j], cur[j - 1]);
    }
    prev = cur;
  }
  return prev[n];
};
const typoBudget = (len) => (len <= 5 ? 1 : len <= 9 ? 2 : 3);
const fuzzyMatches = (item, queryWords) => {
  if (!queryWords.length || queryWords.some((w) => w.length < 4)) return false;
  const nameWords = wordsOf(normalizeSearchText(item.itemName));
  return queryWords.every((qw) =>
    nameWords.some((nw) => nw[0] === qw[0] && Math.abs(nw.length - qw.length) <= 3 && levenshtein(qw, nw) <= typoBudget(qw.length))
  );
};

// Every typed word is found in `text`: words of 3+ letters anywhere inside it,
// 1-2 letter words only at the start of one of its words.
const allWordsIn = (queryWords, text, textWords) =>
  queryWords.length > 0 &&
  queryWords.every((qw) => (qw.length < 3 ? textWords.some((tw) => tw.startsWith(qw)) : text.includes(qw)));

// Score one item against an already-normalized query; Infinity = no match.
const scoreItem = (item, query, queryWords, queryCompact, isCode) => {
  const name = normalizeSearchText(item.itemName);
  const barcode = normalizeSearchText(item.barcode);

  if (barcode && barcode === query) return 0;
  if (isCode && barcode && barcode.startsWith(query)) return 1;
  if (name === query) return 2;
  if (name.startsWith(query)) return 3;

  const nameWords = wordsOf(name);
  if (queryWords.length && queryWords.every((qw) => nameWords.some((nw) => nw.startsWith(qw)))) return 4;
  // 1-2 characters match only at the start of a word (rank 4), never "inside" a word.
  if (query.length >= 3 && (name.includes(query) || (barcode && barcode.includes(query)))) return 5;
  if (queryCompact.length >= 4 && compact(name).includes(queryCompact)) return 5;

  if (allWordsIn(queryWords, name, nameWords)) return 6;
  const categoryName = normalizeSearchText(item.category?.categoryName);
  if (categoryName) {
    const withCategory = `${name} ${categoryName}`;
    if (allWordsIn(queryWords, withCategory, wordsOf(withCategory))) return 7;
  }
  return Infinity;
};

/**
 * Items matching `query`, best match first. Deleted items are dropped. An empty
 * query returns the (non-deleted) items in their given order.
 */
export const rankItems = (items, query) => {
  const live = (items || []).filter((item) => item && !item.isDeleted);
  const q = normalizeSearchText(query);
  if (!q) return live;
  const queryWords = wordsOf(q);
  const queryCompact = compact(q);
  const isCode = looksLikeCode(q);

  const scored = [];
  live.forEach((item) => {
    const score = scoreItem(item, q, queryWords, queryCompact, isCode);
    if (score !== Infinity) scored.push({ item, score });
  });
  // Nothing matched exactly or by words: fall back to a strict typo match.
  if (!scored.length) {
    live.forEach((item) => {
      if (fuzzyMatches(item, queryWords)) scored.push({ item, score: 8 });
    });
  }
  scored.sort(
    (a, b) =>
      a.score - b.score ||
      String(a.item.itemName || "").length - String(b.item.itemName || "").length ||
      String(a.item.itemName || "").localeCompare(String(b.item.itemName || ""), undefined, { sensitivity: "base", numeric: true })
  );
  return scored.map((entry) => entry.item);
};

// True when the typed/scanned text is exactly some item's barcode.
export const hasExactBarcodeMatch = (items, query) => {
  const q = normalizeSearchText(query);
  return !!q && (items || []).some((item) => !item.isDeleted && normalizeSearchText(item.barcode) === q);
};
